<template>
  <div class="asset-manage-page">
    <div class="page-heading">
      <div>
        <div class="heading-title">用户端素材管理</div>
        <div class="heading-meta">USER APP CONTENT</div>
      </div>
      <div class="heading-actions">
        <el-button icon="el-icon-refresh" @click="resetDemoData">恢复演示数据</el-button>
        <el-button type="primary" icon="el-icon-plus" @click="openPageDialog()">新建页面</el-button>
      </div>
    </div>

    <div class="summary-strip">
      <div class="summary-item">
        <span class="summary-icon page-icon"><i class="el-icon-mobile-phone"></i></span>
        <span>
          <strong>{{ pages.length }}</strong>
          <small>页面总数</small>
        </span>
      </div>
      <div class="summary-item">
        <span class="summary-icon asset-icon"><i class="el-icon-collection"></i></span>
        <span>
          <strong>{{ assets.length }}</strong>
          <small>素材总数</small>
        </span>
      </div>
      <div class="summary-item">
        <span class="summary-icon online-icon"><i class="el-icon-circle-check"></i></span>
        <span>
          <strong>{{ enabledAssetCount }}</strong>
          <small>已启用素材</small>
        </span>
      </div>
      <div class="summary-item">
        <span class="summary-icon pending-icon"><i class="el-icon-edit-outline"></i></span>
        <span>
          <strong>{{ disabledAssetCount }}</strong>
          <small>已停用素材</small>
        </span>
      </div>
    </div>

    <div class="workspace">
      <aside class="page-panel">
        <div class="panel-header">
          <div>
            <h2>页面</h2>
            <span>{{ filteredPages.length }} 个页面</span>
          </div>
          <el-tooltip content="新增页面" placement="top">
            <el-button class="icon-button" type="primary" icon="el-icon-plus" circle @click="openPageDialog()"></el-button>
          </el-tooltip>
        </div>

        <div class="page-filter">
          <el-input v-model.trim="pageKeyword" size="small" clearable prefix-icon="el-icon-search" placeholder="搜索页面名称或 key"></el-input>
          <el-select v-model="pageStatus" size="small" aria-label="页面状态">
            <el-option label="全部状态" value="all"></el-option>
            <el-option label="已启用" value="enabled"></el-option>
            <el-option label="已停用" value="disabled"></el-option>
          </el-select>
        </div>

        <div class="page-list">
          <button
            v-for="page in filteredPages"
            :key="page.id"
            type="button"
            :class="['page-list-item', { active: page.id === selectedPageId }]"
            @click="selectPage(page.id)"
          >
            <span class="page-glyph"><i :class="page.icon || 'el-icon-document'"></i></span>
            <span class="page-copy">
              <span class="page-name-row">
                <strong>{{ page.name }}</strong>
                <i :class="['status-dot', page.enabled ? 'is-enabled' : 'is-disabled']"></i>
              </span>
              <code>{{ page.pageKey }}</code>
              <span class="page-count">{{ getPageAssetCount(page.id) }} 项素材</span>
            </span>
            <el-dropdown trigger="click" @command="handlePageCommand($event, page)" @click.native.stop>
              <span class="more-button" @click.stop><i class="el-icon-more"></i></span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="edit" icon="el-icon-edit">编辑页面</el-dropdown-item>
                <el-dropdown-item command="toggle" :icon="page.enabled ? 'el-icon-video-pause' : 'el-icon-video-play'">
                  {{ page.enabled ? '停用页面' : '启用页面' }}
                </el-dropdown-item>
                <el-dropdown-item command="delete" icon="el-icon-delete" divided>删除页面</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </button>
          <div v-if="!filteredPages.length" class="empty-pages">
            <i class="el-icon-search"></i>
            <span>没有匹配的页面</span>
          </div>
        </div>
      </aside>

      <main class="asset-panel">
        <template v-if="selectedPage">
          <div class="asset-header">
            <div class="asset-title-block">
              <div class="asset-title-row">
                <h2>{{ selectedPage.name }}</h2>
                <el-tag :type="selectedPage.enabled ? 'success' : 'info'" size="small" effect="plain">
                  {{ selectedPage.enabled ? '已启用' : '已停用' }}
                </el-tag>
              </div>
              <div class="page-key-line">
                <span>页面 key</span>
                <code>{{ selectedPage.pageKey }}</code>
                <el-tooltip content="复制页面 key" placement="top">
                  <el-button type="text" icon="el-icon-document-copy" @click="copyText(selectedPage.pageKey)"></el-button>
                </el-tooltip>
              </div>
            </div>
            <div class="asset-actions">
              <el-button icon="el-icon-edit" @click="openPageDialog(selectedPage)">编辑页面</el-button>
              <el-button type="primary" icon="el-icon-plus" @click="openAssetDialog()">新增素材</el-button>
            </div>
          </div>

          <div class="asset-toolbar">
            <el-input
              v-model.trim="assetFilters.keyword"
              clearable
              prefix-icon="el-icon-search"
              placeholder="搜索素材名称或素材 key"
              class="keyword-input"
            ></el-input>
            <el-select v-model="assetFilters.type" clearable placeholder="全部类型">
              <el-option v-for="option in assetTypeOptions" :key="option.value" :label="option.label" :value="option.value"></el-option>
            </el-select>
            <el-select v-model="assetFilters.status" placeholder="全部状态">
              <el-option label="全部状态" value="all"></el-option>
              <el-option label="已启用" value="enabled"></el-option>
              <el-option label="已停用" value="disabled"></el-option>
            </el-select>
            <el-button icon="el-icon-refresh-left" @click="resetAssetFilters">重置</el-button>
            <span class="result-count">共 {{ filteredAssets.length }} 项</span>
          </div>

          <el-table
            :data="pagedAssets"
            class="asset-table"
            row-key="id"
            height="calc(100vh - 390px)"
            :header-cell-style="tableHeaderStyle"
          >
            <el-table-column label="素材" min-width="190">
              <template slot-scope="scope">
                <div class="asset-identity">
                  <div :class="['asset-preview', 'type-' + scope.row.type]">
                    <img v-if="scope.row.type === 'image' && scope.row.value && !scope.row.imageError" :src="scope.row.value" alt="" @error="markImageError(scope.row)">
                    <span v-else-if="scope.row.type === 'color'" class="color-swatch" :style="{ backgroundColor: scope.row.value }"></span>
                    <i v-else :class="getAssetTypeIcon(scope.row.type)"></i>
                  </div>
                  <div class="asset-name">
                    <strong>{{ scope.row.name }}</strong>
                    <code>{{ scope.row.assetKey }}</code>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="类型" width="84">
              <template slot-scope="scope">
                <el-tag size="small" effect="plain" :type="getAssetTypeTag(scope.row.type)">
                  {{ getAssetTypeLabel(scope.row.type) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="素材内容" min-width="180">
              <template slot-scope="scope">
                <div class="asset-value" :title="formatAssetValue(scope.row)">{{ formatAssetValue(scope.row) }}</div>
              </template>
            </el-table-column>
            <el-table-column prop="sort" label="排序" width="64" align="center"></el-table-column>
            <el-table-column label="状态" width="88" align="center">
              <template slot-scope="scope">
                <el-switch
                  v-model="scope.row.enabled"
                  active-color="#1f9d68"
                  inactive-color="#c4c9d2"
                  @change="handleAssetStatusChange(scope.row)"
                ></el-switch>
              </template>
            </el-table-column>
            <el-table-column prop="updatedAt" label="更新时间" width="140"></el-table-column>
            <el-table-column label="操作" width="156" align="right">
              <template slot-scope="scope">
                <el-button type="text" icon="el-icon-view" @click="openPreview(scope.row)">预览</el-button>
                <el-button type="text" icon="el-icon-edit" @click="openAssetDialog(scope.row)">编辑</el-button>
                <el-dropdown trigger="click" @command="handleAssetCommand($event, scope.row)">
                  <el-button type="text" class="table-more"><i class="el-icon-more"></i></el-button>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item command="duplicate" icon="el-icon-copy-document">复制素材</el-dropdown-item>
                    <el-dropdown-item command="delete" icon="el-icon-delete" divided>删除素材</el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </template>
            </el-table-column>
          </el-table>

          <div class="pagination-row">
            <span>页面与素材项通过 page key 和 asset key 唯一关联</span>
            <el-pagination
              background
              :current-page.sync="pagination.page"
              :page-size="pagination.size"
              :page-sizes="[10, 20, 50]"
              :total="filteredAssets.length"
              layout="total, sizes, prev, pager, next"
              @size-change="handlePageSizeChange"
            ></el-pagination>
          </div>
        </template>

        <div v-else class="empty-workspace">
          <span class="empty-icon"><i class="el-icon-mobile-phone"></i></span>
          <h2>请选择一个页面</h2>
          <el-button type="primary" icon="el-icon-plus" @click="openPageDialog()">新建页面</el-button>
        </div>
      </main>
    </div>

    <el-dialog
      :title="pageForm.id ? '编辑页面' : '新建页面'"
      :visible.sync="pageDialogVisible"
      width="520px"
      :close-on-click-modal="false"
      @closed="resetPageForm"
    >
      <el-form ref="pageForm" :model="pageForm" :rules="pageRules" label-width="92px" label-position="right">
        <el-form-item label="页面名称" prop="name">
          <el-input v-model.trim="pageForm.name" maxlength="30" show-word-limit placeholder="例如：首页"></el-input>
        </el-form-item>
        <el-form-item label="页面 key" prop="pageKey">
          <el-input v-model.trim="pageForm.pageKey" :disabled="Boolean(pageForm.id)" placeholder="例如：home.index">
            <template slot="prepend">page.</template>
          </el-input>
        </el-form-item>
        <el-form-item label="页面路径" prop="routePath">
          <el-input v-model.trim="pageForm.routePath" placeholder="例如：pages/home/index"></el-input>
        </el-form-item>
        <el-form-item label="页面图标">
          <el-select v-model="pageForm.icon" class="full-width">
            <el-option v-for="option in pageIconOptions" :key="option.value" :label="option.label" :value="option.value">
              <i :class="option.value"></i><span class="icon-option-label">{{ option.label }}</span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="pageForm.enabled">
            <el-radio-button :label="true">启用</el-radio-button>
            <el-radio-button :label="false">停用</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model.trim="pageForm.remark" type="textarea" :rows="3" maxlength="100" show-word-limit placeholder="页面用途或维护说明"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="pageDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitPageForm">保存页面</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="assetForm.id ? '编辑素材' : '新增素材'"
      :visible.sync="assetDialogVisible"
      width="640px"
      :close-on-click-modal="false"
      @closed="resetAssetForm"
    >
      <div class="dialog-context" v-if="selectedPage">
        <i :class="selectedPage.icon"></i>
        <span>{{ selectedPage.name }}</span>
        <code>page.{{ selectedPage.pageKey }}</code>
      </div>
      <el-form ref="assetForm" :model="assetForm" :rules="assetRules" label-width="92px" label-position="right">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="素材名称" prop="name">
              <el-input v-model.trim="assetForm.name" maxlength="30" placeholder="例如：首页主标题"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="素材 key" prop="assetKey">
              <el-input v-model.trim="assetForm.assetKey" :disabled="Boolean(assetForm.id)" placeholder="例如：hero.title"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="素材类型" prop="type">
              <el-select v-model="assetForm.type" class="full-width" @change="handleAssetTypeChange">
                <el-option v-for="option in assetTypeOptions" :key="option.value" :label="option.label" :value="option.value">
                  <i :class="option.icon"></i><span class="icon-option-label">{{ option.label }}</span>
                </el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="排序" prop="sort">
              <el-input-number v-model="assetForm.sort" :min="0" :max="999" controls-position="right" class="full-width"></el-input-number>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="素材内容" prop="value">
          <el-input
            v-if="assetForm.type === 'text' || assetForm.type === 'link'"
            v-model="assetForm.value"
            :type="assetForm.type === 'text' ? 'textarea' : 'text'"
            :rows="4"
            :placeholder="assetForm.type === 'link' ? 'https:// 或用户端页面路径' : '输入展示文案'"
          ></el-input>
          <div v-else-if="assetForm.type === 'image'" class="image-value-editor">
            <el-input v-model.trim="assetForm.value" placeholder="输入图片 URL"></el-input>
            <div class="image-preview-box">
              <img v-if="assetForm.value && !assetForm.imageError" :src="assetForm.value" alt="图片预览" @error="assetForm.imageError = true">
              <span v-else><i class="el-icon-picture-outline"></i>图片预览</span>
            </div>
          </div>
          <div v-else-if="assetForm.type === 'color'" class="color-value-editor">
            <el-color-picker v-model="assetForm.value"></el-color-picker>
            <el-input v-model.trim="assetForm.value" placeholder="#1f9d68"></el-input>
          </div>
          <el-input
            v-else
            v-model="assetForm.value"
            type="textarea"
            :rows="7"
            placeholder='输入合法 JSON，例如：{"title":"物业缴费"}'
          ></el-input>
        </el-form-item>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="状态">
              <el-switch v-model="assetForm.enabled" active-text="启用" inactive-text="停用"></el-switch>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="必填素材">
              <el-switch v-model="assetForm.required" active-text="是" inactive-text="否"></el-switch>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="备注">
          <el-input v-model.trim="assetForm.remark" maxlength="100" show-word-limit placeholder="素材使用位置或尺寸要求"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="assetDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitAssetForm">保存素材</el-button>
      </span>
    </el-dialog>

    <el-dialog title="素材预览" :visible.sync="previewVisible" width="480px" class="preview-dialog">
      <div v-if="previewAsset" class="preview-content">
        <div class="preview-meta">
          <span>{{ selectedPage ? selectedPage.name : '' }}</span>
          <code>{{ previewAsset.assetKey }}</code>
        </div>
        <div :class="['preview-stage', 'preview-' + previewAsset.type]">
          <img v-if="previewAsset.type === 'image' && previewAsset.value" :src="previewAsset.value" alt="素材预览">
          <a v-else-if="previewAsset.type === 'link'" :href="previewAsset.value" target="_blank" rel="noopener noreferrer">{{ previewAsset.value }}</a>
          <span v-else-if="previewAsset.type === 'color'" class="large-color" :style="{ backgroundColor: previewAsset.value }">{{ previewAsset.value }}</span>
          <pre v-else-if="previewAsset.type === 'json'">{{ prettyJson(previewAsset.value) }}</pre>
          <p v-else>{{ previewAsset.value }}</p>
        </div>
        <el-descriptions :column="2" size="small" border>
          <el-descriptions-item label="素材类型">{{ getAssetTypeLabel(previewAsset.type) }}</el-descriptions-item>
          <el-descriptions-item label="当前状态">{{ previewAsset.enabled ? '已启用' : '已停用' }}</el-descriptions-item>
          <el-descriptions-item label="页面 key">{{ selectedPage ? selectedPage.pageKey : '' }}</el-descriptions-item>
          <el-descriptions-item label="素材 key">{{ previewAsset.assetKey }}</el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>
  </div>
</template>

<script>
const STORAGE_KEY = 'propertyadmin:user-app-assets:v1'

const createDemoData = () => ({
  pages: [
    { id: 'p-home', name: '首页', pageKey: 'home.index', routePath: 'pages/home/index', icon: 'el-icon-house', enabled: true, remark: '用户端首页内容' },
    { id: 'p-service', name: '服务', pageKey: 'service.index', routePath: 'pages/service/index', icon: 'el-icon-s-grid', enabled: true, remark: '物业服务入口' },
    { id: 'p-live', name: '生活', pageKey: 'live.index', routePath: 'pages/live/index', icon: 'el-icon-coffee-cup', enabled: true, remark: '社区生活内容' },
    { id: 'p-benefits', name: '权益', pageKey: 'benefits.index', routePath: 'pages/benefits/index', icon: 'el-icon-present', enabled: true, remark: '业主权益页' },
    { id: 'p-user', name: '个人中心', pageKey: 'user.index', routePath: 'pages/user/index', icon: 'el-icon-user', enabled: false, remark: '用户资料和个人服务' }
  ],
  assets: [
    { id: 'a-101', pageId: 'p-home', name: '首页欢迎语', assetKey: 'hero.greeting', type: 'text', value: '上午好，欢迎回家', sort: 10, enabled: true, required: true, remark: '首页顶部欢迎文案', updatedAt: '2026-07-21 10:28' },
    { id: 'a-102', pageId: 'p-home', name: '首页主标题', assetKey: 'hero.title', type: 'text', value: '智慧社区，美好生活', sort: 20, enabled: true, required: true, remark: '', updatedAt: '2026-07-21 10:18' },
    { id: 'a-103', pageId: 'p-home', name: '轮播主图', assetKey: 'banner.primary', type: 'image', value: '', sort: 30, enabled: true, required: false, remark: '建议尺寸 750 x 320', updatedAt: '2026-07-20 17:42' },
    { id: 'a-104', pageId: 'p-home', name: '公告入口文案', assetKey: 'notice.entry_label', type: 'text', value: '社区公告', sort: 40, enabled: true, required: true, remark: '', updatedAt: '2026-07-20 15:06' },
    { id: 'a-105', pageId: 'p-home', name: '主题色', assetKey: 'theme.primary_color', type: 'color', value: '#1f9d68', sort: 50, enabled: true, required: false, remark: '', updatedAt: '2026-07-19 13:12' },
    { id: 'a-201', pageId: 'p-service', name: '页面标题', assetKey: 'header.title', type: 'text', value: '社区服务', sort: 10, enabled: true, required: true, remark: '', updatedAt: '2026-07-21 09:44' },
    { id: 'a-202', pageId: 'p-service', name: '服务分组配置', assetKey: 'service.groups', type: 'json', value: '{"primary":["repair","payment"],"secondary":["visitor","parking"]}', sort: 20, enabled: true, required: true, remark: '服务入口分组', updatedAt: '2026-07-20 16:30' },
    { id: 'a-301', pageId: 'p-live', name: '生活频道标题', assetKey: 'channel.title', type: 'text', value: '邻里生活', sort: 10, enabled: true, required: true, remark: '', updatedAt: '2026-07-18 11:20' },
    { id: 'a-302', pageId: 'p-live', name: '活动列表链接', assetKey: 'activity.more_url', type: 'link', value: '/pages/activity/list', sort: 20, enabled: true, required: false, remark: '', updatedAt: '2026-07-18 10:52' },
    { id: 'a-401', pageId: 'p-benefits', name: '权益页说明', assetKey: 'header.subtitle', type: 'text', value: '专属权益，尽在社区', sort: 10, enabled: true, required: false, remark: '', updatedAt: '2026-07-17 16:08' },
    { id: 'a-501', pageId: 'p-user', name: '未登录提示', assetKey: 'guest.message', type: 'text', value: '登录后查看您的社区服务', sort: 10, enabled: false, required: true, remark: '', updatedAt: '2026-07-16 14:22' }
  ]
})

export default {
  name: 'UserAppAssetManageList',
  data() {
    const validateKey = (rule, value, callback) => {
      if (!/^[a-z][a-z0-9]*(\.[a-z0-9_]+)*$/.test(value || '')) {
        callback(new Error('仅支持小写字母、数字、下划线和英文点号，且必须以字母开头'))
        return
      }
      callback()
    }
    return {
      pages: [],
      assets: [],
      selectedPageId: '',
      pageKeyword: '',
      pageStatus: 'all',
      assetFilters: { keyword: '', type: '', status: 'all' },
      pagination: { page: 1, size: 10 },
      pageDialogVisible: false,
      assetDialogVisible: false,
      previewVisible: false,
      previewAsset: null,
      pageForm: this.emptyPageForm(),
      assetForm: this.emptyAssetForm(),
      pageIconOptions: [
        { label: '首页', value: 'el-icon-house' },
        { label: '应用', value: 'el-icon-s-grid' },
        { label: '生活', value: 'el-icon-coffee-cup' },
        { label: '权益', value: 'el-icon-present' },
        { label: '用户', value: 'el-icon-user' },
        { label: '文档', value: 'el-icon-document' }
      ],
      assetTypeOptions: [
        { label: '文本', value: 'text', icon: 'el-icon-tickets' },
        { label: '图片', value: 'image', icon: 'el-icon-picture-outline' },
        { label: '链接', value: 'link', icon: 'el-icon-link' },
        { label: '颜色', value: 'color', icon: 'el-icon-brush' },
        { label: 'JSON', value: 'json', icon: 'el-icon-data-line' }
      ],
      pageRules: {
        name: [{ required: true, message: '请输入页面名称', trigger: 'blur' }],
        pageKey: [
          { required: true, message: '请输入页面 key', trigger: 'blur' },
          { validator: validateKey, trigger: 'blur' }
        ],
        routePath: [{ required: true, message: '请输入用户端页面路径', trigger: 'blur' }]
      },
      assetRules: {
        name: [{ required: true, message: '请输入素材名称', trigger: 'blur' }],
        assetKey: [
          { required: true, message: '请输入素材 key', trigger: 'blur' },
          { validator: validateKey, trigger: 'blur' }
        ],
        type: [{ required: true, message: '请选择素材类型', trigger: 'change' }],
        value: [{ required: true, message: '请输入素材内容', trigger: 'blur' }]
      },
      tableHeaderStyle: {
        background: '#f7f8fa',
        color: '#606874',
        fontWeight: '600'
      }
    }
  },
  computed: {
    selectedPage() {
      return this.pages.find(page => page.id === this.selectedPageId) || null
    },
    enabledAssetCount() {
      return this.assets.filter(asset => asset.enabled).length
    },
    disabledAssetCount() {
      return this.assets.length - this.enabledAssetCount
    },
    filteredPages() {
      const keyword = this.pageKeyword.toLowerCase()
      return this.pages.filter(page => {
        const matchesKeyword = !keyword || page.name.toLowerCase().includes(keyword) || page.pageKey.toLowerCase().includes(keyword)
        const matchesStatus = this.pageStatus === 'all' || (this.pageStatus === 'enabled' ? page.enabled : !page.enabled)
        return matchesKeyword && matchesStatus
      })
    },
    filteredAssets() {
      const keyword = this.assetFilters.keyword.toLowerCase()
      return this.assets
        .filter(asset => asset.pageId === this.selectedPageId)
        .filter(asset => !keyword || asset.name.toLowerCase().includes(keyword) || asset.assetKey.toLowerCase().includes(keyword))
        .filter(asset => !this.assetFilters.type || asset.type === this.assetFilters.type)
        .filter(asset => this.assetFilters.status === 'all' || (this.assetFilters.status === 'enabled' ? asset.enabled : !asset.enabled))
        .sort((a, b) => a.sort - b.sort)
    },
    pagedAssets() {
      const start = (this.pagination.page - 1) * this.pagination.size
      return this.filteredAssets.slice(start, start + this.pagination.size)
    }
  },
  watch: {
    'assetFilters.keyword'() {
      this.pagination.page = 1
    },
    'assetFilters.type'() {
      this.pagination.page = 1
    },
    'assetFilters.status'() {
      this.pagination.page = 1
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    emptyPageForm() {
      return { id: '', name: '', pageKey: '', routePath: '', icon: 'el-icon-document', enabled: true, remark: '' }
    },
    emptyAssetForm() {
      return { id: '', name: '', assetKey: '', type: 'text', value: '', sort: 10, enabled: true, required: false, remark: '', imageError: false }
    },
    loadData() {
      let data
      try {
        data = JSON.parse(localStorage.getItem(STORAGE_KEY))
      } catch (error) {
        data = null
      }
      if (!data || !Array.isArray(data.pages) || !Array.isArray(data.assets)) {
        data = createDemoData()
      }
      this.pages = data.pages
      this.assets = data.assets
      this.selectedPageId = this.pages.length ? this.pages[0].id : ''
      this.persistData()
    },
    persistData() {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ pages: this.pages, assets: this.assets }))
    },
    resetDemoData() {
      this.$confirm('将清除当前浏览器中的页面和素材修改，确定恢复演示数据吗？', '恢复演示数据', {
        confirmButtonText: '恢复',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const data = createDemoData()
        this.pages = data.pages
        this.assets = data.assets
        this.selectedPageId = this.pages[0].id
        this.persistData()
        this.$message.success('演示数据已恢复')
      }).catch(() => {})
    },
    getPageAssetCount(pageId) {
      return this.assets.filter(asset => asset.pageId === pageId).length
    },
    selectPage(pageId) {
      this.selectedPageId = pageId
      this.resetAssetFilters()
    },
    openPageDialog(page) {
      this.pageForm = page ? { ...page } : this.emptyPageForm()
      this.pageDialogVisible = true
    },
    resetPageForm() {
      if (this.$refs.pageForm) this.$refs.pageForm.resetFields()
      this.pageForm = this.emptyPageForm()
    },
    submitPageForm() {
      this.$refs.pageForm.validate(valid => {
        if (!valid) return
        const duplicated = this.pages.some(page => page.pageKey === this.pageForm.pageKey && page.id !== this.pageForm.id)
        if (duplicated) {
          this.$message.error('页面 key 已存在')
          return
        }
        if (this.pageForm.id) {
          const index = this.pages.findIndex(page => page.id === this.pageForm.id)
          this.$set(this.pages, index, { ...this.pageForm })
        } else {
          const page = { ...this.pageForm, id: `p-${Date.now()}` }
          this.pages.unshift(page)
          this.selectedPageId = page.id
        }
        this.persistData()
        this.pageDialogVisible = false
        this.$message.success(this.pageForm.id ? '页面已更新' : '页面已创建')
      })
    },
    handlePageCommand(command, page) {
      if (command === 'edit') {
        this.openPageDialog(page)
      } else if (command === 'toggle') {
        page.enabled = !page.enabled
        this.persistData()
        this.$message.success(page.enabled ? '页面已启用' : '页面已停用')
      } else if (command === 'delete') {
        this.deletePage(page)
      }
    },
    deletePage(page) {
      const assetCount = this.getPageAssetCount(page.id)
      this.$confirm(`删除页面后将同时移除其下 ${assetCount} 项素材，确定继续吗？`, '删除页面', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.pages = this.pages.filter(item => item.id !== page.id)
        this.assets = this.assets.filter(item => item.pageId !== page.id)
        if (this.selectedPageId === page.id) this.selectedPageId = this.pages.length ? this.pages[0].id : ''
        this.persistData()
        this.$message.success('页面已删除')
      }).catch(() => {})
    },
    openAssetDialog(asset) {
      this.assetForm = asset ? { ...asset, imageError: false } : this.emptyAssetForm()
      this.assetDialogVisible = true
    },
    resetAssetForm() {
      if (this.$refs.assetForm) this.$refs.assetForm.resetFields()
      this.assetForm = this.emptyAssetForm()
    },
    handleAssetTypeChange() {
      this.assetForm.value = this.assetForm.type === 'color' ? '#1f9d68' : ''
      this.assetForm.imageError = false
    },
    submitAssetForm() {
      this.$refs.assetForm.validate(valid => {
        if (!valid) return
        if (this.assetForm.type === 'json') {
          try {
            JSON.parse(this.assetForm.value)
          } catch (error) {
            this.$message.error('请输入合法的 JSON 内容')
            return
          }
        }
        const duplicated = this.assets.some(asset => asset.pageId === this.selectedPageId && asset.assetKey === this.assetForm.assetKey && asset.id !== this.assetForm.id)
        if (duplicated) {
          this.$message.error('当前页面下已存在相同的素材 key')
          return
        }
        const savedAsset = {
          ...this.assetForm,
          pageId: this.selectedPageId,
          updatedAt: this.nowText()
        }
        delete savedAsset.imageError
        if (savedAsset.id) {
          const index = this.assets.findIndex(asset => asset.id === savedAsset.id)
          this.$set(this.assets, index, savedAsset)
        } else {
          savedAsset.id = `a-${Date.now()}`
          this.assets.push(savedAsset)
        }
        this.persistData()
        this.assetDialogVisible = false
        this.$message.success(this.assetForm.id ? '素材已更新' : '素材已创建')
      })
    },
    handleAssetStatusChange(asset) {
      asset.updatedAt = this.nowText()
      this.persistData()
      this.$message.success(asset.enabled ? '素材已启用' : '素材已停用')
    },
    handleAssetCommand(command, asset) {
      if (command === 'duplicate') {
        const duplicate = {
          ...asset,
          id: `a-${Date.now()}`,
          name: `${asset.name} 副本`,
          assetKey: this.nextAssetKey(asset.assetKey),
          sort: asset.sort + 1,
          updatedAt: this.nowText()
        }
        this.assets.push(duplicate)
        this.persistData()
        this.$message.success('素材已复制')
      } else if (command === 'delete') {
        this.deleteAsset(asset)
      }
    },
    nextAssetKey(assetKey) {
      const base = `${assetKey}_copy`
      let candidate = base
      let index = 2
      while (this.assets.some(asset => asset.pageId === this.selectedPageId && asset.assetKey === candidate)) {
        candidate = `${base}_${index}`
        index += 1
      }
      return candidate
    },
    deleteAsset(asset) {
      this.$confirm(`确定删除素材“${asset.name}”吗？`, '删除素材', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.assets = this.assets.filter(item => item.id !== asset.id)
        this.persistData()
        this.$message.success('素材已删除')
      }).catch(() => {})
    },
    openPreview(asset) {
      this.previewAsset = asset
      this.previewVisible = true
    },
    resetAssetFilters() {
      this.assetFilters = { keyword: '', type: '', status: 'all' }
      this.pagination.page = 1
    },
    handlePageSizeChange(size) {
      this.pagination.size = size
      this.pagination.page = 1
    },
    getAssetTypeLabel(type) {
      const option = this.assetTypeOptions.find(item => item.value === type)
      return option ? option.label : type
    },
    getAssetTypeIcon(type) {
      const option = this.assetTypeOptions.find(item => item.value === type)
      return option ? option.icon : 'el-icon-document'
    },
    getAssetTypeTag(type) {
      return { text: '', image: 'success', link: 'primary', color: 'warning', json: 'info' }[type] || 'info'
    },
    formatAssetValue(asset) {
      if (!asset.value) return '未设置'
      if (asset.type === 'json') return this.prettyJson(asset.value).replace(/\s+/g, ' ')
      return asset.value
    },
    prettyJson(value) {
      try {
        return JSON.stringify(JSON.parse(value), null, 2)
      } catch (error) {
        return value
      }
    },
    markImageError(asset) {
      this.$set(asset, 'imageError', true)
    },
    copyText(value) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).then(() => this.$message.success('页面 key 已复制'))
      } else {
        this.$message.info(value)
      }
    },
    nowText() {
      const now = new Date()
      const pad = value => String(value).padStart(2, '0')
      return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`
    }
  }
}
</script>

<style lang="scss" scoped>
.asset-manage-page {
  min-height: calc(100vh - 60px);
  padding: 18px 22px 22px;
  background: #f3f5f7;
  color: #262b33;
  box-sizing: border-box;
}

.page-heading,
.asset-header,
.asset-toolbar,
.panel-header,
.page-name-row,
.pagination-row,
.heading-actions,
.asset-actions,
.asset-title-row,
.page-key-line,
.dialog-context {
  display: flex;
  align-items: center;
}

.page-heading {
  justify-content: space-between;
  min-height: 52px;
  margin-bottom: 14px;
}

.heading-title {
  font-size: 21px;
  line-height: 28px;
  font-weight: 650;
}

.heading-meta {
  margin-top: 2px;
  color: #9aa1ab;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0;
}

.heading-actions,
.asset-actions {
  gap: 10px;
}

.summary-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(150px, 1fr));
  min-height: 82px;
  margin-bottom: 14px;
  border: 1px solid #e2e5e9;
  background: #fff;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 22px;
  border-right: 1px solid #edf0f2;
}

.summary-item:last-child {
  border-right: 0;
}

.summary-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 6px;
  font-size: 18px;
}

.page-icon { color: #266bdc; background: #edf4ff; }
.asset-icon { color: #7856c7; background: #f3effc; }
.online-icon { color: #13845b; background: #eaf8f2; }
.pending-icon { color: #b76b18; background: #fff5e8; }

.summary-item strong,
.summary-item small {
  display: block;
}

.summary-item strong {
  font-size: 23px;
  line-height: 26px;
}

.summary-item small {
  margin-top: 3px;
  color: #858c96;
  font-size: 12px;
}

.workspace {
  display: grid;
  grid-template-columns: 292px minmax(0, 1fr);
  min-height: 600px;
  border: 1px solid #dfe3e8;
  background: #fff;
}

.page-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-right: 1px solid #dfe3e8;
  background: #fbfcfd;
}

.panel-header {
  justify-content: space-between;
  height: 68px;
  padding: 0 16px 0 18px;
  border-bottom: 1px solid #e7eaee;
  box-sizing: border-box;
}

.panel-header h2,
.asset-title-row h2,
.empty-workspace h2 {
  margin: 0;
  font-size: 16px;
  line-height: 22px;
}

.panel-header span {
  color: #9299a3;
  font-size: 12px;
}

.icon-button {
  width: 32px;
  height: 32px;
  padding: 0;
}

.page-filter {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 94px;
  gap: 8px;
  padding: 12px;
  border-bottom: 1px solid #e8ebef;
}

.page-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.page-list-item {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) 24px;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 78px;
  margin: 0 0 4px;
  padding: 10px;
  border: 1px solid transparent;
  border-radius: 5px;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  box-sizing: border-box;
}

.page-list-item:hover {
  background: #f1f4f7;
}

.page-list-item.active {
  border-color: #c9dbf8;
  background: #edf4ff;
}

.page-glyph {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  background: #fff;
  color: #4d6685;
  font-size: 17px;
  box-shadow: 0 1px 2px rgba(24, 39, 58, 0.08);
}

.active .page-glyph {
  background: #266bdc;
  color: #fff;
}

.page-copy,
.asset-name {
  min-width: 0;
}

.page-copy,
.page-copy code,
.page-count,
.asset-name strong,
.asset-name code {
  display: block;
}

.page-name-row {
  justify-content: space-between;
  gap: 8px;
}

.page-name-row strong {
  overflow: hidden;
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-dot {
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.is-enabled { background: #1f9d68; }
.is-disabled { background: #b9bec6; }

.page-copy code,
.asset-name code,
.page-key-line code,
.dialog-context code,
.preview-meta code {
  overflow: hidden;
  color: #6f7783;
  font-family: Consolas, Monaco, monospace;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.page-copy code { margin-top: 3px; }
.page-count { margin-top: 4px; color: #9aa1aa; font-size: 11px; }

.more-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 28px;
  color: #8d949e;
}

.empty-pages,
.empty-workspace {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: #a1a7af;
}

.empty-pages {
  min-height: 180px;
  gap: 8px;
  font-size: 12px;
}

.empty-pages i { font-size: 24px; }

.asset-panel {
  min-width: 0;
  background: #fff;
}

.asset-header {
  justify-content: space-between;
  min-height: 68px;
  padding: 0 20px;
  border-bottom: 1px solid #e7eaee;
  box-sizing: border-box;
}

.asset-title-row {
  gap: 10px;
}

.page-key-line {
  gap: 7px;
  margin-top: 4px;
  color: #9198a2;
  font-size: 11px;
}

.page-key-line code {
  color: #526173;
}

.page-key-line .el-button {
  padding: 0;
}

.asset-toolbar {
  gap: 10px;
  min-height: 66px;
  padding: 12px 20px;
  border-bottom: 1px solid #edf0f3;
  box-sizing: border-box;
}

.asset-toolbar .keyword-input { width: min(320px, 34%); }
.asset-toolbar .el-select { width: 132px; }
.result-count { margin-left: auto; color: #858c95; font-size: 12px; white-space: nowrap; }

.asset-table::before { display: none; }

.asset-table::v-deep td,
.asset-table::v-deep th {
  padding: 10px 0;
}

.asset-identity {
  display: flex;
  align-items: center;
  gap: 11px;
}

.asset-preview {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  overflow: hidden;
  border: 1px solid #e1e5e9;
  border-radius: 5px;
  background: #f4f6f8;
  color: #6c7888;
  font-size: 17px;
}

.asset-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.asset-preview.type-link { color: #266bdc; background: #edf4ff; }
.asset-preview.type-json { color: #7856c7; background: #f3effc; }
.asset-preview.type-text { color: #4d6685; background: #f1f4f7; }

.color-swatch {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.08);
}

.asset-name strong {
  overflow: hidden;
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.asset-value {
  overflow: hidden;
  color: #5f6670;
  font-size: 12px;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table-more {
  margin-left: 10px;
}

.pagination-row {
  justify-content: space-between;
  min-height: 64px;
  padding: 0 20px;
  border-top: 1px solid #edf0f3;
  box-sizing: border-box;
}

.pagination-row > span {
  color: #9aa1aa;
  font-size: 11px;
}

.empty-workspace {
  min-height: 540px;
  gap: 14px;
}

.empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #f0f3f6;
  color: #8090a2;
  font-size: 30px;
}

.full-width { width: 100%; }
.icon-option-label { margin-left: 8px; }

.dialog-context {
  gap: 8px;
  margin: -4px 0 18px 92px;
  color: #59616c;
  font-size: 13px;
}

.dialog-context code {
  padding-left: 8px;
  border-left: 1px solid #dfe3e7;
}

.image-value-editor,
.color-value-editor {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.image-value-editor .el-input { flex: 1; }

.image-preview-box {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 112px;
  height: 74px;
  overflow: hidden;
  border: 1px dashed #c8ced6;
  border-radius: 5px;
  background: #f8f9fa;
  color: #9ba2ac;
  font-size: 11px;
}

.image-preview-box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-preview-box span,
.image-preview-box i {
  display: block;
  text-align: center;
}

.image-preview-box i {
  margin-bottom: 5px;
  font-size: 22px;
}

.color-value-editor .el-input { width: 180px; }

.preview-content {
  min-height: 260px;
}

.preview-meta {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  color: #69717d;
  font-size: 12px;
}

.preview-stage {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 170px;
  margin-bottom: 18px;
  padding: 22px;
  overflow: auto;
  border: 1px solid #e2e6ea;
  background: #f7f8fa;
  box-sizing: border-box;
}

.preview-stage p {
  margin: 0;
  color: #252b33;
  font-size: 24px;
  line-height: 1.5;
  text-align: center;
  word-break: break-word;
}

.preview-stage img {
  max-width: 100%;
  max-height: 260px;
  object-fit: contain;
}

.preview-stage a {
  color: #266bdc;
  word-break: break-all;
}

.preview-stage pre {
  width: 100%;
  margin: 0;
  color: #46515f;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}

.large-color {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 180px;
  height: 110px;
  padding: 10px;
  border-radius: 5px;
  color: #fff;
  font-family: Consolas, Monaco, monospace;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
  box-sizing: border-box;
}

@media (max-width: 1180px) {
  .summary-strip { grid-template-columns: repeat(2, minmax(150px, 1fr)); }
  .summary-item:nth-child(2) { border-right: 0; }
  .summary-item:nth-child(-n + 2) { border-bottom: 1px solid #edf0f2; }
  .workspace { grid-template-columns: 260px minmax(0, 1fr); }
  .asset-toolbar { flex-wrap: wrap; }
  .asset-toolbar .keyword-input { width: 100%; }
  .result-count { margin-left: 0; }
}
</style>
